import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';
import _ from 'underscore';

import {getArchiveItemName} from 'archive/ArchivePurposesUtil';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {REQUIRED_FIELD_ERROR_MESSAGE} from 'notebook/tables/excess/Util';
import notifications from 'notifications';
import * as Colors from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import AddIcon from 'ui-library/icons/AddIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {withLoadingScreen} from 'util/Promises';
import ValidationRulesModal from 'validation_rules/modals/ValidationRulesModal';
import {UpdateValidationRulesParams, ValidationRuleParams} from 'validation_rules/network/types';

import {ValidationRulesQuery, ValidationRulesQueryVariables} from './__generated/ValidationRulesQuery';

export type AdvancedRule = ExcludeInaccessible<
  ValidationRulesQuery['hasValidationRulesItem']
>['advancedRules'][number];

type SimpleRule = ExcludeInaccessible<ValidationRulesQuery['hasValidationRulesItem']>['simpleRules'][number];

interface ProcessedSimpleRule {
  validationRuleId: string;
  schemaFieldApiIdentifier: string;
  schemaFields: SimpleRule['schemaFields'];
  errorMessage: Array<string>;
  name: string;
}

export type ValidationRule = AdvancedRule | ProcessedSimpleRule;

export const VALIDATION_RULES_QUERY = gql`
  query ValidationRulesQuery($id: StrictString!) @skipAudit @engComponent(name: VALIDATION_RULES_ENGINE) {
    hasValidationRulesItem(id: $id) {
      ... on HasValidationRulesInterface {
        id
        advancedRules {
          ... on ValidationRuleNode {
            id
            name
            userInputFormula
            errorMessage
            description
            overridable
            conditionData {
              benchlingExpression {
                ... on BenchlingExpressionNode {
                  expressionJson
                }
              }
            }
            schemaFields {
              ... on SchemaFieldNode {
                id
                displayName
              }
            }
            ...CoreObjectArchivableGraphQL
          }
        }
        simpleRules {
          ... on SimpleRuleNode {
            validationRuleId
            schemaFieldApiIdentifier
            validationRuleTemplateId
            constantInputs
            schemaFields {
              ... on SchemaFieldNode {
                id
                displayName
                isRequired
                isMulti
              }
            }
            errorMessage
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('CoreObjectArchivable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ValidationRulesTableProps {
  validationRules: Array<ValidationRule>;
  hasValidationRulesItemId: string;
  onSubmit: (validationRulesParams: UpdateValidationRulesParams) => Promise<void>;
  refetchValidationRules: () => void;
  isShowingArchived: boolean;
  onShowArchived: () => void;
  archivedRulesCount: number;
}

function isAdvancedRule(rule: AdvancedRule | ProcessedSimpleRule): rule is AdvancedRule {
  return (rule as AdvancedRule).id !== undefined && (rule as AdvancedRule).userInputFormula !== undefined;
}

function sortValidationRules(rules: Array<ValidationRule>): Array<ValidationRule> {
  return rules.sort((a, b) => {
    const getFieldName = (rule: ValidationRule): string => rule.schemaFields?.[0]?.displayName || '';

    const getApiIdentifier = (rule: ValidationRule): string => {
      if (isAdvancedRule(rule)) {
        return rule.id;
      } else {
        return rule.validationRuleId;
      }
    };

    const fieldComparison = getFieldName(a).localeCompare(getFieldName(b));
    if (fieldComparison !== 0) {
      return fieldComparison;
    }

    const nameComparison = a.name.localeCompare(b.name);
    if (nameComparison !== 0) {
      return nameComparison;
    }

    return getApiIdentifier(a).localeCompare(getApiIdentifier(b));
  });
}

export function ValidationRulesTable(props: ValidationRulesTableProps): JSX.Element {
  async function createValidationRule(params: ValidationRuleParams): Promise<void> {
    await withLoadingScreen(
      props.onSubmit({
        id: props.hasValidationRulesItemId,
        advancedValidationRules: [{...params, operation: 'create'}],
      }),
      'Creating rule...'
    );
    props.refetchValidationRules();
  }

  function getCreateUpdateValidationRuleCallback(
    validationRuleId: string
  ): (params: ValidationRuleParams) => Promise<void> {
    return async (params: ValidationRuleParams): Promise<void> => {
      await withLoadingScreen(
        props.onSubmit({
          id: props.hasValidationRulesItemId,
          advancedValidationRules: [
            {
              id: validationRuleId,
              ...params,
              operation: 'update',
            },
          ],
        }),
        'Updating rule...'
      );
      props.refetchValidationRules();
    };
  }

  function onArchiveValidationRule(validationRuleId: string): void {
    const ruleToArchive = props.validationRules.find(
      (rule) => isAdvancedRule(rule) && rule.id === validationRuleId
    ) as AdvancedRule | undefined;
    if (!ruleToArchive) {
      return;
    }
    ArchiveModal.open({
      items: [ruleToArchive],
      onArchive: async (itemIds: Array<string>, purpose: string): Promise<void> => {
        await withLoadingScreen(
          props.onSubmit({
            id: props.hasValidationRulesItemId,
            advancedValidationRules: [
              {
                id: itemIds[0],
                operation: 'update',
                isArchived: true,
                archiveReason: purpose,
              },
            ],
          }),
          'Archiving rule...'
        );
        props.refetchValidationRules();
      },
    });
  }

  async function onUnarchiveValidationRule(validationRuleId: string): Promise<void> {
    await withLoadingScreen(
      props.onSubmit({
        id: props.hasValidationRulesItemId,
        advancedValidationRules: [
          {
            id: validationRuleId,
            operation: 'update',
            isArchived: false,
          },
        ],
      }),
      'Unarchiving rule...'
    );
    props.refetchValidationRules();
  }

  function isShowArchivedRulesRow(rowIndex: number): boolean {
    return rowIndex === props.validationRules.length && shouldShowArchivedRow;
  }

  const shouldShowArchivedRow = !props.isShowingArchived && props.archivedRulesCount > 0;
  const totalRows = props.validationRules.length + (shouldShowArchivedRow ? 1 : 0);

  return (
    <Table
      numRows={totalRows}
      emptyTableText="This schema does not have any validation rules."
      getRowClassName={(rowIndex: number) => {
        const baseClass = css(styles.tableRow);
        if (rowIndex < props.validationRules.length) {
          const rule = props.validationRules[rowIndex];
          const isRuleArchived = isAdvancedRule(rule) && isArchived({...rule, id: rule.id});
          return isRuleArchived ? classNames(baseClass, css(styles.archivedRule)) : baseClass;
        }
        return baseClass;
      }}
    >
      <Column
        header={
          <Cell>
            Rules
            <AddIcon
              onClick={() => {
                runModal(ValidationRulesModal, {
                  hasValidationRulesItemId: props.hasValidationRulesItemId,
                  onRequestClose: noop,
                  onRequestSubmit: createValidationRule,
                });
              }}
            />
          </Cell>
        }
        renderCell={(rowIndex: number) => {
          if (isShowArchivedRulesRow(rowIndex)) {
            return (
              <Cell>
                <a
                  href=""
                  onClick={(e) => {
                    e.preventDefault();
                    props.onShowArchived();
                  }}
                >
                  Show archived validation rules ({props.archivedRulesCount})
                </a>
              </Cell>
            );
          }
          const rule = props.validationRules[rowIndex];
          const displayName = isAdvancedRule(rule)
            ? getArchiveItemName({...rule, name: rule.name})
            : rule.name;
          return displayName;
        }}
      />
      <Column
        header="Relevant Fields"
        renderCell={(rowIndex: number) => {
          if (isShowArchivedRulesRow(rowIndex)) {
            return '';
          }
          return props.validationRules[rowIndex].schemaFields.map(({displayName}) => displayName).join(', ');
        }}
      />
      <Column
        header="Error Message"
        renderCell={(rowIndex: number) => {
          if (isShowArchivedRulesRow(rowIndex)) {
            return '';
          }
          const rule = props.validationRules[rowIndex];
          if (!isAdvancedRule(rule)) {
            return (
              <Cell>
                {rule.errorMessage.map((error, index) => (
                  <React.Fragment key={index}>
                    {error}
                    {index < rule.errorMessage.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Cell>
            );
          } else {
            return rule.errorMessage;
          }
        }}
      />

      <Column
        renderCell={(rowIndex: number) => {
          if (isShowArchivedRulesRow(rowIndex)) {
            return <Cell className="u-text-align-right" />;
          }
          const rule = props.validationRules[rowIndex];
          if (!isAdvancedRule(rule)) {
            return <Cell className="u-text-align-right" />;
          }

          return (
            <Cell className="u-text-align-right">
              <div className="u-display-inline-block">
                <BenchlingIcon
                  className={classNames('updateIcon', css(styles.updateIcon))}
                  onClick={() => {
                    runModal(ValidationRulesModal, {
                      hasValidationRulesItemId: props.hasValidationRulesItemId,
                      onRequestClose: noop,
                      onRequestSubmit: getCreateUpdateValidationRuleCallback(rule.id),
                      name: rule.name,
                      description: rule.description,
                      errorMessage: rule.errorMessage,
                      overridable: rule.overridable,
                      userInputFormula: rule.userInputFormula,
                    });
                  }}
                  icon="edit"
                />
                {isArchived({...rule, id: rule.id}) ? (
                  <BenchlingIcon
                    className={classNames('unarchiveIcon', 'u-spacing-left', css(styles.unarchiveIcon))}
                    onClick={() => onUnarchiveValidationRule(rule.id)}
                    icon="unarchive"
                  />
                ) : (
                  <BenchlingIcon
                    className={classNames('archiveIcon', 'u-spacing-left', css(styles.archiveIcon))}
                    onClick={() => {
                      onArchiveValidationRule(rule.id);
                    }}
                    icon="archive"
                  />
                )}
              </div>
            </Cell>
          );
        }}
      />
    </Table>
  );
}

function processSimpleValidationRules(
  simpleRules: SimpleRule[] | null | undefined
): Array<ProcessedSimpleRule> {
  return Array.from(
    (simpleRules || [])
      .reduce((map, rule) => {
        const key = rule.schemaFieldApiIdentifier;
        const existing = map.get(key);

        if (existing) {
          existing.errorMessage.push(rule.errorMessage || '');
        } else {
          const errorMessages = [];
          errorMessages.push(rule.errorMessage || '');
          if (rule.schemaFields?.[0]?.isRequired) {
            errorMessages.push(REQUIRED_FIELD_ERROR_MESSAGE);
          }

          map.set(key, {
            validationRuleId: rule.validationRuleId,
            schemaFieldApiIdentifier: rule.schemaFieldApiIdentifier,
            schemaFields: rule.schemaFields,
            errorMessage: errorMessages,
            name: `${rule.schemaFields[0]?.displayName || 'Unknown Field'} Rules`,
          });
        }

        return map;
      }, new Map<string, ProcessedSimpleRule>())
      .values()
  );
}

type QueryingValidationRulesTableProps = Omit<
  ValidationRulesTableProps,
  'validationRules' | 'refetchValidationRules' | 'isShowingArchived' | 'onShowArchived' | 'archivedRulesCount'
>;

export default function QueryingValidationRulesTable(
  props: QueryingValidationRulesTableProps
): JSX.Element | null {
  const [isShowingArchived, setIsShowingArchived] = useState(false);

  const result = useBenchlingQuery<ValidationRulesQuery, ValidationRulesQueryVariables>(
    VALIDATION_RULES_QUERY,
    {
      variables: {id: props.hasValidationRulesItemId},
    }
  );
  if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    notifications.error('Error loading validation rules. Try refreshing the page.');
    return null;
  } else if (isInaccessible(result.data?.hasValidationRulesItem)) {
    return <div>This item is inaccessible.</div>;
  } else {
    const simpleValidationRules = processSimpleValidationRules(
      result.data?.hasValidationRulesItem.simpleRules
    );

    const advancedValidationRules = result.data?.hasValidationRulesItem.advancedRules;
    const combinedValidationRules = sortValidationRules([
      ...simpleValidationRules,
      ...advancedValidationRules,
    ]);

    const allValidationRules = isInaccessible(result.data?.hasValidationRulesItem)
      ? []
      : combinedValidationRules;

    const [archivedRules, nonArchivedRules]: [Array<ValidationRule>, Array<ValidationRule>] = _.partition(
      allValidationRules,
      (rule) => {
        if (isAdvancedRule(rule)) {
          return isArchived({...rule, id: rule.id});
        }
        return false;
      }
    ) as [Array<ValidationRule>, Array<ValidationRule>];

    const validationRules = isShowingArchived
      ? [...nonArchivedRules, ...archivedRules] // Non-archived first, then archived
      : nonArchivedRules;

    return (
      <ValidationRulesTable
        {...props}
        validationRules={validationRules}
        refetchValidationRules={result.refetch}
        isShowingArchived={isShowingArchived}
        onShowArchived={() => {
          setIsShowingArchived(true);
        }}
        archivedRulesCount={archivedRules.length}
      />
    );
  }
}

const styles = StyleSheet.create({
  tableRow: {
    ':hover .archiveIcon': {
      visibility: 'visible',
    },
    ':hover .updateIcon': {
      visibility: 'visible',
    },
    ':hover .unarchiveIcon': {
      visibility: 'visible',
    },
  },
  archivedRule: {
    opacity: 0.6,
  },
  updateIcon: {
    color: Colors.GRAY_DARK,
    visibility: 'hidden',
    cursor: 'pointer',
    ':hover': {
      color: Colors.BLUE_LIGHT,
      visibility: 'visible',
    },
  },
  archiveIcon: {
    color: Colors.GRAY_DARK,
    visibility: 'hidden',
    cursor: 'pointer',
    ':hover': {
      color: Colors.RED_LIGHT,
      visibility: 'visible',
    },
  },
  unarchiveIcon: {
    color: Colors.GRAY_DARK,
    visibility: 'hidden',
    cursor: 'pointer',
    ':hover': {
      color: Colors.BLUE_LIGHT,
      visibility: 'visible',
    },
  },
});
